import { createContext, useContext, useState, useEffect } from 'react';

var CartContext = createContext({});
var CART_KEY = 'aml_cart';

function loadCart() {
  try {
    var stored = localStorage.getItem(CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch(e) { return []; }
}

function persistCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
  var [items, setItems] = useState(loadCart);
  var [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(function() { persistCart(items); }, [items]);

  function addItem(product, variant, colour) {
    setItems(function(prev) {
      var key = product.id + '_' + (variant ? variant.id : '') + '_' + (colour ? colour.id : '');
      var existing = prev.find(function(item) { return item.key === key; });
      if (existing) {
        return prev.map(function(item) {
          return item.key === key ? Object.assign({}, item, { quantity: item.quantity + 1 }) : item;
        });
      }
      return prev.concat([{
        key: key,
        productId: product.id,
        name: product.name,
        slug: product.slug,
        variantId: variant ? variant.id : null,
        variantName: variant ? variant.size_name : '',
        colourId: colour ? colour.id : null,
        colourName: colour ? colour.name : '',
        colourHex: colour ? colour.hex_code : '',
        price: variant ? variant.price : product.base_price,
        quantity: 1,
        maxStock: product.stock,
      }]);
    });
    setDrawerOpen(true);
  }

  function updateQuantity(key, quantity) {
    if (quantity < 1) { removeItem(key); return; }
    setItems(function(prev) {
      return prev.map(function(item) {
        if (item.key === key) {
          return Object.assign({}, item, { quantity: Math.min(quantity, item.maxStock || 99) });
        }
        return item;
      });
    });
  }

  function removeItem(key) {
    setItems(function(prev) {
      return prev.filter(function(item) { return item.key !== key; });
    });
  }

  function clearCart() {
    setItems([]);
  }

  // The "Return to your bag" email link (/bag/restore/:token): puts the
  // saved lines back. A line already in the bag keeps the larger quantity.
  function restoreItems(lines) {
    setItems(function(prev) {
      var next = prev.slice();
      lines.forEach(function(line) {
        var i = next.findIndex(function(item) { return item.key === line.key; });
        if (i < 0) { next.push(line); return; }
        var cap = line.maxStock || next[i].maxStock || 99;
        next[i] = Object.assign({}, next[i], line, { quantity: Math.min(Math.max(next[i].quantity, line.quantity), cap) });
      });
      return next;
    });
  }

  // Refresh the bag against current live products: update prices and names,
  // remove anything no longer live, sold out, or no longer offered in that size/colour.
  function syncWithProducts(liveProducts) {
    var removed = 0;
    var changed = 0;
    var next = [];
    items.forEach(function(item) {
      var product = liveProducts.find(function(p) { return p.id === item.productId; });
      if (!product || Number(product.stock) <= 0) { removed++; return; }
      var variants = product.variants || [];
      var colours = product.colours || [];
      var variant = variants.length ? variants.find(function(v) { return v.id === item.variantId; }) : null;
      if (variants.length && !variant) { removed++; return; }
      var colour = colours.length ? colours.find(function(c) { return c.id === item.colourId; }) : null;
      if (colours.length && !colour) { removed++; return; }
      var price = Number(variant ? variant.price : product.base_price);
      var quantity = Math.min(item.quantity, Number(product.stock));
      if (price !== Number(item.price) || quantity !== item.quantity) changed++;
      next.push(Object.assign({}, item, {
        name: product.name,
        price: price,
        quantity: quantity,
        maxStock: product.stock
      }));
    });
    if (removed > 0 || changed > 0) setItems(next);
    return { removed: removed, changed: changed };
  }

  var itemCount = items.reduce(function(sum, item) { return sum + item.quantity; }, 0);
  var subtotal = items.reduce(function(sum, item) { return sum + (item.price * item.quantity); }, 0);

  function openDrawer() { setDrawerOpen(true); }
  function closeDrawer() { setDrawerOpen(false); }

  return (
    <CartContext.Provider value={{
      items: items,
      itemCount: itemCount,
      subtotal: subtotal,
      addItem: addItem,
      updateQuantity: updateQuantity,
      removeItem: removeItem,
      clearCart: clearCart,
      restoreItems: restoreItems,
      syncWithProducts: syncWithProducts,
      drawerOpen: drawerOpen,
      openDrawer: openDrawer,
      closeDrawer: closeDrawer
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
